import { createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import { toast } from "sonner";
import type { RootState } from "../store";
import {
  saveViewPreference,
  saveViewPreferenceKeepalive,
  saveViewPreferenceWithRetry,
} from "@/features/settings/api/viewPreferences.api";
import {
  setUiDensityMode,
  hydrateUiDensity,
} from "@/features/settings/stores/uiDensitySlice";
import { setDensityMode, DENSITY_VIEW_KEY } from "@/lib/uiDensity";
import {
  setDrawerTabOrder,
  DRAWER_TABS_VIEW_KEY,
} from "@/features/settings/stores/managePreferencesSlice";
import {
  setLeadBranchToggle,
  setLeadStatusToggle,
  setLeadEmailToggle,
  setLeadFollowUpToggle,
  setLeadPaymentStatusToggle,
  setLeadLeadSourceToggle,
  setLeadCampaignToggle,
  setLeadSubstatusToggle,
  setLeadPriorityToggle,
  setLeadLastUpdatedOnToggle,
  setLeadCreatedAtToggle,
  setLeadLastCallRecordToggle,
  setLeadUsersToggle,
  setLeadRemarksToggle,
  setLeadLeadScoreToggle,
  toggleLeadCustomField,
  toggleLeadModule,
  toggleLeadRole,
  setLeadListColumnOrder,
  setLeadListColumnFilter,
  setLeadListColumnSort,
  clearLeadListColumnFilters,
  setLeadListColumnWidth,
  toggleLeadListColumnPin,
  resetLeadCardLayout,
} from "@/features/leads/stores/leadCardFunctionalitySlice";
import {
  setDynamicStatusToggle,
  setDynamicEmailToggle,
  setDynamicFollowUpToggle,
  setDynamicPaymentStatusToggle,
  setDynamicLeadSourceToggle,
  setDynamicCampaignToggle,
  setDynamicSubstatusToggle,
  setDynamicPriorityToggle,
  setDynamicLastUpdatedOnToggle,
  setDynamicCreatedAtToggle,
  setDynamicLastCallRecordToggle,
  setDynamicUsersToggle,
  setDynamicRemarksToggle,
  setDynamicLeadScoreToggle,
  toggleDynamicCustomField,
  toggleDynamicModule,
  toggleDynamicRole,
  setDynamicListColumnOrder,
  setDynamicListColumnFilter,
  setDynamicListColumnSort,
  clearDynamicListColumnFilters,
  setDynamicListColumnWidth,
  toggleDynamicListColumnPin,
  seedDynamicCardLayoutFromView,
  resetDynamicCardLayout,
} from "@/features/dynamic/store/DynamicCardFunctionalitySlice";

/**
 * Write-through sync for the per-user "Show/hide fields" view preferences.
 * Whenever the user changes a column-visibility toggle, debounce ~800ms and
 * PUT the whole slice blob to the DB (the source of truth). redux-persist
 * still mirrors it to localStorage for instant first paint.
 *
 * Gated by `enabled`: write-through stays off until boot hydration finishes
 * (see `enableViewPrefSync`), so neither the redux-persist REHYDRATE nor the
 * server-hydrate dispatches echo a save straight back to the server.
 */
export const viewPrefSyncMiddleware = createListenerMiddleware();

let enabled = false;
export function enableViewPrefSync() {
  enabled = true;
}

const DEBOUNCE_MS = 800;

/**
 * UNLOAD SAFETY NET. A change sits in the 800ms debounce (or in a failed
 * save) as a PENDING blob per viewKey. If the tab is closed, reloaded or
 * backgrounded before the debounced PUT lands, `pagehide` / a hidden
 * `visibilitychange` flushes every pending blob with a keepalive fetch, so
 * a column arrangement / header filter made seconds before closing the
 * laptop still reaches the DB. Cleared the moment a normal save succeeds.
 */
const pending = new Map<string, unknown>();
/** viewKeys already reported as failing, so one outage isn't a toast storm. */
const warnedKeys = new Set<string>();
function markPending(viewKey: string, config: unknown) {
  pending.set(viewKey, config);
}
function flushPending() {
  if (pending.size === 0) return;
  for (const [viewKey, config] of pending) {
    if (saveViewPreferenceKeepalive(viewKey, config)) pending.delete(viewKey);
  }
}
if (typeof window !== "undefined") {
  window.addEventListener("pagehide", flushPending);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushPending();
  });
}

/** Debounced write-through with retries; the blob stays pending until saved. */
async function persistSlice(
  viewKey: string,
  listenerApi: { cancelActiveListeners: () => void; delay: (ms: number) => Promise<void>; getState: () => unknown },
  pick: (state: RootState) => unknown,
) {
  // Record the LATEST blob immediately so an unload inside the debounce
  // window still flushes it.
  markPending(viewKey, stripPersist(pick(listenerApi.getState() as RootState)));
  listenerApi.cancelActiveListeners();
  await listenerApi.delay(DEBOUNCE_MS);
  const config = stripPersist(pick(listenerApi.getState() as RootState));
  markPending(viewKey, config);
  try {
    await saveViewPreferenceWithRetry(viewKey, config);
    // Only drop the pending copy if nothing newer was recorded meanwhile.
    if (pending.get(viewKey) === config) pending.delete(viewKey);
    warnedKeys.delete(viewKey);
  } catch (err) {
    // Stays pending: the unload flush (or the next change) will carry it.
    // Say so ONCE per key — a layout that silently stops saving is the exact
    // "it isn't persistent" complaint this whole path exists to prevent.
    if (!warnedKeys.has(viewKey)) {
      warnedKeys.add(viewKey);
      console.error(`[viewPrefSync] "${viewKey}" could not be saved:`, err);
      toast.error(
        "Couldn't save your layout to the server — it will retry when you make the next change.",
      );
    }
  }
}

// Drop redux-persist's bookkeeping key so the DB blob stays clean.
function stripPersist(slice: unknown): unknown {
  if (!slice || typeof slice !== "object") return slice;
  const rest: Record<string, unknown> = { ...(slice as Record<string, unknown>) };
  delete rest._persist;
  return rest;
}

// Main Leads page layout.
viewPrefSyncMiddleware.startListening({
  matcher: isAnyOf(
    setLeadBranchToggle,
    setLeadStatusToggle,
    setLeadEmailToggle,
    setLeadFollowUpToggle,
    setLeadPaymentStatusToggle,
    setLeadLeadSourceToggle,
    setLeadCampaignToggle,
    setLeadSubstatusToggle,
    setLeadPriorityToggle,
    setLeadLastUpdatedOnToggle,
    setLeadCreatedAtToggle,
    setLeadLastCallRecordToggle,
    setLeadUsersToggle,
    setLeadRemarksToggle,
    setLeadLeadScoreToggle,
    toggleLeadCustomField,
    toggleLeadModule,
    toggleLeadRole,
    // List-view header drag-and-drop + header-menu filters/sort ride the
    // same blob.
    setLeadListColumnOrder,
    setLeadListColumnFilter,
    setLeadListColumnSort,
    clearLeadListColumnFilters,
    setLeadListColumnWidth,
    toggleLeadListColumnPin,
    resetLeadCardLayout,
  ),
  effect: async (_action, listenerApi) => {
    if (!enabled) return;
    await persistSlice(
      "leadCardFunctionality",
      listenerApi,
      (state) => state.leadCardFunctionality,
    );
  },
});

// Per dynamic-tab layouts (route map).
viewPrefSyncMiddleware.startListening({
  matcher: isAnyOf(
    setDynamicStatusToggle,
    setDynamicEmailToggle,
    setDynamicFollowUpToggle,
    setDynamicPaymentStatusToggle,
    setDynamicLeadSourceToggle,
    setDynamicCampaignToggle,
    setDynamicSubstatusToggle,
    setDynamicPriorityToggle,
    setDynamicLastUpdatedOnToggle,
    setDynamicCreatedAtToggle,
    setDynamicLastCallRecordToggle,
    setDynamicUsersToggle,
    setDynamicRemarksToggle,
    setDynamicLeadScoreToggle,
    toggleDynamicCustomField,
    toggleDynamicModule,
    toggleDynamicRole,
    setDynamicListColumnOrder,
    setDynamicListColumnFilter,
    setDynamicListColumnSort,
    clearDynamicListColumnFilters,
    setDynamicListColumnWidth,
    toggleDynamicListColumnPin,
    // A favorite view applying its saved columns (and the `seededFromView`
    // stamp that stops it re-applying) must reach the DB too, or another
    // device would apply the view again over the user's later toggles.
    seedDynamicCardLayoutFromView,
    resetDynamicCardLayout,
  ),
  effect: async (_action, listenerApi) => {
    if (!enabled) return;
    await persistSlice(
      "dynamicCardFunctionality",
      listenerApi,
      // `shared-<token>` routes are share links being VIEWED — their setup
      // belongs to the link, not to this user's saved layouts.
      (state) =>
        Object.fromEntries(
          Object.entries(state.dynamicCardFunctionality ?? {}).filter(
            ([routeId]) => !String(routeId).startsWith("shared-"),
          ),
        ),
    );
  },
});

/**
 * UI density (per-user, DB-only — no redux-persist mirror).
 *
 * This listener also owns the DOM side-effect, so the reducer stays pure: the
 * root font-size is applied on BOTH hydrate and user change, but only a
 * user-initiated change is written back to the server (`enabled` + the action
 * check together stop the boot hydrate from echoing a save).
 */
viewPrefSyncMiddleware.startListening({
  matcher: isAnyOf(setUiDensityMode, hydrateUiDensity),
  effect: async (action, listenerApi) => {
    // Apply immediately — density must never lag the click behind a debounce.
    const mode = (listenerApi.getState() as RootState).uiDensity.mode;
    setDensityMode(mode);

    if (!enabled || !setUiDensityMode.match(action)) return;
    listenerApi.cancelActiveListeners();
    await listenerApi.delay(DEBOUNCE_MS);
    const latest = (listenerApi.getState() as RootState).uiDensity.mode;
    try {
      await saveViewPreference(DENSITY_VIEW_KEY, { mode: latest });
    } catch {
      // Best-effort: the mode is already applied for this session.
    }
  },
});

/**
 * Lead-view drawer tab order (per-user, DB-backed; redux-persist keeps a
 * first-paint mirror). Every drag lands in the DB via the same debounced,
 * retried, unload-flushed path as the list layouts, so the arrangement
 * follows the user to any browser or device. An admin's save also becomes
 * the workspace default (server-side mirror) — the same rule as the list
 * layouts — so new users open the admin's arrangement.
 */
viewPrefSyncMiddleware.startListening({
  matcher: isAnyOf(setDrawerTabOrder),
  effect: async (_action, listenerApi) => {
    if (!enabled) return;
    await persistSlice(DRAWER_TABS_VIEW_KEY, listenerApi, (state) => ({
      order: state.managePreferences.drawerTabOrder ?? [],
    }));
  },
});
